import React, { useState } from 'react';
import { Button, Text, Flex, Loader, Alert } from '@mantine/core';
import { IconRefresh, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { uploadAndMakePublicAvatar } from '../../utils/publicAvatarUtils';
import { useUser } from '../../contexts/UserContext';

/**
 * 사용자 아바타 수동 싱크 버튼 컴포넌트
 * 새로운 Public URL 시스템 사용 (Firebase Auth 사용 안 함)
 */
export const AvatarSyncButton = ({ size = 'sm', variant = 'light', fullWidth = false }) => {
  const { profile } = useUser();
  const [isLoading, setIsLoading] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const handleSyncAvatar = async () => {
    if (!profile?.id) {
      setSyncResult({
        success: false,
        message: '로그인이 필요합니다.'
      });
      return;
    }

    setIsLoading(true);
    setSyncResult(null);

    try {
      const accessToken = localStorage.getItem('accessToken');
      if (!accessToken) {
        throw new Error('액세스 토큰이 없습니다. 다시 로그인해 주세요.');
      }

      // 1. 최신 프로필 정보 가져오기
      const response = await fetch(
        `https://graph.threads.net/v1.0/me?fields=threads_profile_picture_url&access_token=${accessToken}`
      );
      
      if (!response.ok) {
        throw new Error(`Threads API error: ${response.status}`);
      }
      
      const profileData = await response.json();
      const latestImageURL = profileData.threads_profile_picture_url;
      
      if (!latestImageURL) {
        throw new Error('프로필 이미지 URL을 찾을 수 없습니다.');
      }

      // 2. 새로운 Public URL 시스템으로 업로드
      console.log('🔄 Uploading avatar to public URL system...');
      const publicAvatarURL = await uploadAndMakePublicAvatar(profile.id, latestImageURL);
      
      setSyncResult({
        success: true,
        updated: true,
        message: '프로필 이미지가 업데이트되었습니다.',
        url: publicAvatarURL
      });

      // 성공 시 3초 후 메시지 자동 숨김
      setTimeout(() => {
        setSyncResult(null);
      }, 3000);

    } catch (error) {
      console.error('Avatar sync error:', error);
      setSyncResult({
        success: false,
        message: `오류가 발생했습니다: ${error.message}`
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonProps = () => {
    if (isLoading) {
      return {
        color: 'blue',
        leftSection: <Loader size={16} />,
        disabled: true
      };
    }

    if (syncResult?.success && syncResult?.updated) {
      return {
        color: 'green',
        leftSection: <IconCheck size={16} />,
        disabled: false
      };
    }

    return {
      color: 'blue',
      leftSection: <IconRefresh size={16} />,
      disabled: false
    };
  };

  const getButtonText = () => {
    if (isLoading) return '업로드 중...';
    if (syncResult?.success && syncResult?.updated) return '업데이트 완료';
    return '프로필 이미지 업데이트';
  };

  return (
    <Flex direction="column" gap={8}>
      <Button
        size={size}
        variant={variant}
        fullWidth={fullWidth}
        onClick={handleSyncAvatar}
        {...getButtonProps()}
      >
        {getButtonText()}
      </Button>

      {syncResult && (
        <Alert
          icon={syncResult.success ? <IconCheck size={16} /> : <IconAlertCircle size={16} />}
          color={syncResult.success ? 'green' : 'red'}
          variant="light"
          size="sm"
        >
          <Text size="sm">
            {syncResult.message}
          </Text>
        </Alert>
      )}

      <Text size="xs" c="dimmed" ta="center">
        Threads에서 프로필 이미지를 변경한 경우 클릭해 주세요
      </Text>
    </Flex>
  );
};

export default AvatarSyncButton;